import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../lib/CartContext";
import { useAuth } from "../lib/AuthContext";
import { useSettings } from "../lib/SettingsContext";
import { http, currency, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { CreditCard, Building2, Loader2, Truck, MapPin, Banknote } from "lucide-react";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { user } = useAuth();
  const { items, subtotal, clear } = useCart();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("transferencia");
  const [deliveryMethod, setDeliveryMethod] = useState("domicilio");
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [ivaEnabled, setIvaEnabled] = useState(false);
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_phone: user?.phone || "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zip: "",
    guest_email: user?.email || "",
    notes: "",
  });

  const shippingFee = Number(settings?.shipping_fee_domicilio ?? 99);
  const freeThreshold = Number(settings?.shipping_free_threshold ?? 0);
  const shipping = deliveryMethod === "sucursal"
    ? 0
    : (freeThreshold > 0 && subtotal >= freeThreshold ? 0 : shippingFee);
  const IVA_RATE = 0.16;
  const iva = ivaEnabled ? Math.round(subtotal * IVA_RATE * 100) / 100 : 0;
  const total = subtotal + shipping + iva;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (items.length === 0) {
      toast.error("El carrito está vacío");
      return;
    }
    const required = ["shipping_name", "shipping_phone"];
    if (deliveryMethod === "domicilio") required.push("shipping_address");
    for (const k of required) {
      if (!form[k]) {
        const msg = deliveryMethod === "domicilio"
          ? "Completa nombre, WhatsApp y dirección"
          : "Completa nombre y WhatsApp para tu recolección";
        toast.error(msg);
        return;
      }
    }
    setLoading(true);
    try {
      const orderItems = items.map((it) => ({
        product_id: it.product_id,
        quantity: it.quantity,
        color_value: it.color_value,
        color_label: it.color_label,
        size_value: it.size_value,
        size_label: it.size_label,
        custom_width: it.custom_width,
        custom_height: it.custom_height,
        design_url: it.design_url,
        design_filename: it.design_filename,
        design_position: it.design_position,
        printing_method: it.printing_method,
        printing_method_label: it.printing_method_label,
        with_design_service: !!it.with_design_service,
        notes: it.notes,
      }));
      const { data: order } = await http.post("/orders", {
        items: orderItems,
        payment_method: paymentMethod,
        delivery_method: deliveryMethod,
        shipping_name: form.shipping_name,
        shipping_phone: form.shipping_phone,
        shipping_address: deliveryMethod === "domicilio" ? form.shipping_address : "",
        shipping_city: form.shipping_city,
        shipping_state: form.shipping_state,
        shipping_zip: form.shipping_zip,
        notes: form.notes,
        iva_enabled: ivaEnabled,
        ...(form.guest_email ? { guest_email: form.guest_email } : {}),
      });

      if (paymentMethod === "stripe") {
        const { data: co } = await http.post("/payments/checkout", {
          order_id: order.order_id,
          origin_url: window.location.origin,
        });
        clear();
        window.location.href = co.checkout_url;
      } else {
        clear();
        const msg = paymentMethod === "efectivo"
          ? "Pedido creado — Paga en efectivo al recibir o al recolectar en sucursal."
          : "Pedido creado — Revisa las instrucciones de transferencia";
        toast.success(msg);
        nav(`/pedido/${order.order_id}${paymentMethod === "transferencia" ? "?tab=transferencia" : ""}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl">Tu carrito está vacío</h1>
        <Button onClick={() => nav("/tienda")} className="brutal-btn bg-black text-white mt-6">Ir a la tienda</Button>
      </div>
    );
  }

  const pickupAddress = [settings?.company_address, settings?.company_city].filter(Boolean).join(", ");

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="checkout-page">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase text-neutral-500">/checkout</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-1">Finalizar pedido</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Delivery method */}
          <div className="brutal-card p-6">
            <h2 className="font-display text-2xl tracking-tight mb-4">Método de entrega</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <button
                onClick={() => setDeliveryMethod("domicilio")}
                className={`p-4 border text-left transition-colors ${deliveryMethod === "domicilio" ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"}`}
                data-testid="checkout-delivery-domicilio"
              >
                <Truck className="w-5 h-5" />
                <div className="font-display text-lg mt-2 tracking-tight">Entrega a domicilio</div>
                <div className="text-xs opacity-70 mt-1">
                  Envío a todo México · {currency(shippingFee)}
                  {freeThreshold > 0 ? ` (gratis desde ${currency(freeThreshold)})` : ""}.
                </div>
              </button>
              <button
                onClick={() => setDeliveryMethod("sucursal")}
                className={`p-4 border text-left transition-colors ${deliveryMethod === "sucursal" ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"}`}
                data-testid="checkout-delivery-sucursal"
              >
                <MapPin className="w-5 h-5" />
                <div className="font-display text-lg mt-2 tracking-tight">Recolección en sucursal</div>
                <div className="text-xs opacity-70 mt-1">Te avisamos cuando esté listo para recoger.</div>
              </button>
            </div>
            {deliveryMethod === "sucursal" && (
              <div className="mt-4 border border-neutral-200 bg-neutral-50 p-4 text-sm" data-testid="checkout-pickup-info">
                <div className="text-xs font-mono uppercase text-neutral-500 mb-1">Dirección de la sucursal</div>
                <div className="font-medium">{settings?.company_name || "Nuestra sucursal"}</div>
                <div className="text-neutral-600 mt-1">{pickupAddress || "Contáctanos para coordinar la recolección."}</div>
                {settings?.company_hours && <div className="text-xs text-neutral-500 mt-1 font-mono">Horario: {settings.company_hours}</div>}
              </div>
            )}
          </div>

          <div className="brutal-card p-6">
            <h2 className="font-display text-2xl tracking-tight mb-1">
              {deliveryMethod === "sucursal" ? "Datos de contacto" : "Datos de entrega"}
            </h2>
            <p className="text-xs text-neutral-500 font-mono mb-4">
              {deliveryMethod === "sucursal"
                ? "Necesitamos tu nombre y WhatsApp para avisarte cuando esté listo."
                : (user ? "Solo necesitamos 3 datos para entregarte tu pedido." : "No necesitas cuenta — solo llena los datos y listo.")}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Nombre completo</Label>
                <Input value={form.shipping_name} onChange={(e) => set("shipping_name", e.target.value)}
                       className="rounded-none mt-2" data-testid="checkout-name" placeholder="María López García" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">WhatsApp</Label>
                <Input value={form.shipping_phone} onChange={(e) => set("shipping_phone", e.target.value)}
                       className="rounded-none mt-2" data-testid="checkout-phone" placeholder="55 1234 5678" />
                <div className="text-xs text-neutral-500 mt-1">Te contactamos por aquí para confirmar tu pedido.</div>
              </div>
              {!user && (
                <div>
                  <Label className="text-xs font-mono uppercase">Correo (opcional)</Label>
                  <Input type="email" value={form.guest_email} onChange={(e) => set("guest_email", e.target.value)}
                         className="rounded-none mt-2" data-testid="checkout-email" placeholder="tu@correo.com" />
                </div>
              )}
              {deliveryMethod === "domicilio" && (
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Dirección de entrega</Label>
                  <Textarea rows={2} value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)}
                         className="rounded-none mt-2" placeholder="Calle, número, colonia, ciudad, estado y código postal" data-testid="checkout-address" />
                </div>
              )}

              {deliveryMethod === "domicilio" && (
                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setShowExtraFields((v) => !v)}
                    className="text-xs font-mono uppercase text-neutral-500 hover:text-black"
                    data-testid="checkout-extra-toggle"
                  >
                    {showExtraFields ? "− Ocultar" : "+ Datos adicionales (opcional)"}
                  </button>
                </div>
              )}
              {deliveryMethod === "domicilio" && showExtraFields && (
                <>
                  <div>
                    <Label className="text-xs font-mono uppercase">Ciudad</Label>
                    <Input value={form.shipping_city} onChange={(e) => set("shipping_city", e.target.value)}
                           className="rounded-none mt-2" data-testid="checkout-city" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Estado</Label>
                    <Input value={form.shipping_state} onChange={(e) => set("shipping_state", e.target.value)}
                           className="rounded-none mt-2" data-testid="checkout-state" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Código Postal</Label>
                    <Input value={form.shipping_zip} onChange={(e) => set("shipping_zip", e.target.value)}
                           className="rounded-none mt-2" data-testid="checkout-zip" />
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Notas del pedido (opcional)</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                          className="rounded-none mt-2" rows={2} data-testid="checkout-notes" />
              </div>
            </div>
          </div>

          <div className="brutal-card p-6">
            <h2 className="font-display text-2xl tracking-tight mb-4">Método de pago</h2>
            <div className="grid md:grid-cols-3 gap-3">
              <button
                onClick={() => setPaymentMethod("stripe")}
                className={`p-4 border text-left transition-colors ${paymentMethod === "stripe" ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"}`}
                data-testid="checkout-pay-stripe"
              >
                <CreditCard className="w-5 h-5" />
                <div className="font-display text-lg mt-2 tracking-tight">Tarjeta</div>
                <div className="text-xs opacity-70 mt-1">Débito o crédito vía Stripe.</div>
              </button>
              <button
                onClick={() => setPaymentMethod("transferencia")}
                className={`p-4 border text-left transition-colors ${paymentMethod === "transferencia" ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"}`}
                data-testid="checkout-pay-transfer"
              >
                <Building2 className="w-5 h-5" />
                <div className="font-display text-lg mt-2 tracking-tight">Transferencia</div>
                <div className="text-xs opacity-70 mt-1">Paga por SPEI. Enviamos instrucciones.</div>
              </button>
              <button
                onClick={() => setPaymentMethod("efectivo")}
                className={`p-4 border text-left transition-colors ${paymentMethod === "efectivo" ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"}`}
                data-testid="checkout-pay-cash"
              >
                <Banknote className="w-5 h-5" />
                <div className="font-display text-lg mt-2 tracking-tight">Efectivo</div>
                <div className="text-xs opacity-70 mt-1">
                  {deliveryMethod === "sucursal" ? "Paga al recoger en sucursal." : "Paga al momento de la entrega."}
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="brutal-card p-6 lg:sticky lg:top-24">
            <div className="text-xs font-mono uppercase text-neutral-500">Tu pedido</div>
            <div className="mt-4 space-y-2 max-h-64 overflow-auto">
              {items.map((it) => (
                <div key={it.cart_id} className="flex justify-between text-sm gap-2">
                  <span className="truncate">{it.quantity}× {it.product_name}</span>
                  <span className="font-mono flex-shrink-0">{currency(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="h-px bg-neutral-200 my-4" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{currency(subtotal)}</span></div>
              <div className="flex justify-between" data-testid="checkout-shipping-line">
                <span>{deliveryMethod === "sucursal" ? "Recolección" : "Envío"}</span>
                <span>
                  {deliveryMethod === "sucursal"
                    ? "Sin costo"
                    : shipping === 0 ? "Gratis" : currency(shipping)}
                </span>
              </div>
              {/* IVA toggle */}
              <label
                className="flex items-center justify-between gap-2 py-2 px-2 border border-dashed border-neutral-300 hover:bg-neutral-50 cursor-pointer text-xs"
                data-testid="checkout-iva-toggle"
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ivaEnabled}
                    onChange={(e) => setIvaEnabled(e.target.checked)}
                    className="w-4 h-4 accent-black"
                    data-testid="checkout-iva-check"
                  />
                  <span className="font-medium">Facturar con IVA (16%)</span>
                </span>
                <span className="font-mono text-neutral-600">
                  {ivaEnabled ? `+ ${currency(iva)}` : "—"}
                </span>
              </label>
              {ivaEnabled && (
                <div className="flex justify-between text-neutral-600" data-testid="checkout-iva-line">
                  <span>IVA (16%)</span>
                  <span>{currency(iva)}</span>
                </div>
              )}
              <div className="flex justify-between font-display text-2xl mt-3"><span>Total</span><span>{currency(total)}</span></div>
            </div>
            <Button
              onClick={submit}
              disabled={loading}
              size="lg"
              className="w-full h-12 mt-6 brutal-btn bg-[#FF3B30] hover:bg-black text-white"
              data-testid="checkout-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {paymentMethod === "stripe" ? "Pagar con tarjeta" : "Confirmar pedido"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
